import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../db/firebase";
import {
  HiOutlineStar,
  HiOutlineCalendarDays,
  HiOutlineMusicalNote,
  HiOutlineXMark,
  HiOutlineEye,
} from "react-icons/hi2";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import { SongArtwork } from "../../../components/SongArtwork";
import { useAuth } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";

interface TopSongRow {
  songId: string;
  title: string;
  artist: string;
  album: string;
  artworkURL: string | null;
  count: number;
}

interface RelatedEvent {
  id: string;
  title?: string;
  date?: string;
  status?: string;
}

function normalizeSongIds(songs: unknown): string[] {
  if (!Array.isArray(songs)) return [];
  return songs
    .map((s) => {
      if (typeof s === "string") return s.trim();
      if (s && typeof s === "object" && "id" in s) {
        return String((s as { id: string }).id).trim();
      }
      return "";
    })
    .filter(Boolean);
}

/**
 * Collect unique song IDs scheduled on one event.
 * - Worship / playlist events: top-level `songs` array (IDs)
 * - Service events: also songs nested under `serviceOrder` worship items
 * A song counts at most once per event.
 */
function songIdsFromEvent(eventData: Record<string, any>): string[] {
  const ids = new Set<string>();

  normalizeSongIds(eventData.songs).forEach((id) => ids.add(id));

  const serviceOrder = Array.isArray(eventData.serviceOrder)
    ? eventData.serviceOrder
    : [];
  serviceOrder.forEach((item: any) => {
    if (!item || typeof item !== "object") return;
    normalizeSongIds(item.songs).forEach((id) => ids.add(id));
  });

  return Array.from(ids);
}

const TopSongs: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, organizationId } = useAuth();
  const [topSongs, setTopSongs] = useState<TopSongRow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState<TopSongRow | null>(null);
  const [relatedEvents, setRelatedEvents] = useState<RelatedEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTopSongs = async () => {
      if (!organizationId) {
        setTopSongs([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // All org events (Active + Archived)
        const eventsSnapshot = await getDocs(
          query(
            collection(db, "events"),
            where("organizationId", "==", organizationId),
          ),
        );

        // Rank = how many distinct org events include the song on their setlist.
        const songCount: Record<string, number> = {};
        let eventsWithSongs = 0;

        eventsSnapshot.forEach((eventDoc) => {
          const songIds = songIdsFromEvent(eventDoc.data());
          if (songIds.length === 0) return;
          eventsWithSongs += 1;
          songIds.forEach((songId) => {
            songCount[songId] = (songCount[songId] || 0) + 1;
          });
        });

        const sortedSongs = Object.entries(songCount)
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .slice(0, 5);

        const songDetails = (
          await Promise.all(
            sortedSongs.map(async ([songId, count]) => {
              const songDoc = await getDoc(doc(db, "songs", songId));
              // Skip orphaned IDs that no longer exist in the library
              if (!songDoc.exists()) return null;
              const songData = songDoc.data();
              return {
                songId,
                title: songData?.title || t("unknownTitle") || "Unknown title",
                artist:
                  songData?.artist || t("unknownArtist") || "Unknown artist",
                album: songData?.album || "",
                artworkURL: songData?.artworkURL || null,
                count,
              } satisfies TopSongRow;
            }),
          )
        ).filter((row): row is TopSongRow => row !== null);

        // If orphans dropped us under 5, fill from next ranked IDs
        if (songDetails.length < 5) {
          const used = new Set(songDetails.map((s) => s.songId));
          const remaining = Object.entries(songCount)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .filter(([id]) => !used.has(id));

          for (const [songId, count] of remaining) {
            if (songDetails.length >= 5) break;
            const songDoc = await getDoc(doc(db, "songs", songId));
            if (!songDoc.exists()) continue;
            const songData = songDoc.data();
            songDetails.push({
              songId,
              title: songData?.title || t("unknownTitle") || "Unknown title",
              artist:
                songData?.artist || t("unknownArtist") || "Unknown artist",
              album: songData?.album || "",
              artworkURL: songData?.artworkURL || null,
              count,
            });
          }
        }

        if (!cancelled) {
          setTopSongs(songDetails);
          if (process.env.NODE_ENV === "development") {
            console.debug(
              `[TopSongs] org=${organizationId} events=${eventsSnapshot.size} withSongs=${eventsWithSongs} uniqueSongs=${Object.keys(songCount).length}`,
            );
          }
        }
      } catch (err) {
        console.error("Error fetching top songs:", err);
        if (!cancelled) setTopSongs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTopSongs();
    return () => {
      cancelled = true;
    };
  }, [organizationId, t]);

  const handleOpenModal = async (song: TopSongRow) => {
    setSelectedSong(song);
    setModalOpen(true);
    setRelatedEvents([]);
    setEventsLoading(true);

    try {
      // Prefer org-scoped events, then filter client-side for the song.
      // array-contains alone can miss orgs / composite-index issues.
      const eventsSnapshot = await getDocs(
        query(
          collection(db, "events"),
          where("organizationId", "==", organizationId),
        ),
      );
      const events = eventsSnapshot.docs
        .filter((d) => songIdsFromEvent(d.data()).includes(song.songId))
        .map((d) => ({
          id: d.id,
          title: d.data().title,
          date: d.data().date,
          status: d.data().status,
        }))
        .sort((a, b) =>
          String(b.date || "").localeCompare(String(a.date || "")),
        );
      setRelatedEvents(events);
    } catch (err) {
      console.error("Error fetching events:", err);
      setRelatedEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSong(null);
    setRelatedEvents([]);
  };

  const glassBtn =
    "inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors";

  return (
    <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden">
      <div className="px-4 sm:px-5 py-3.5 sm:py-4 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center gap-2.5">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA] shrink-0">
          <HiOutlineMusicalNote className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
            {t("topSongsFromEvents") || "Top 5 songs from events"}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {t("topSongsFromEventsHint") ||
              "Ranked by how many events include each song"}
          </p>
        </div>
        {!loading && topSongs.length > 0 && (
          <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500 shrink-0">
            {t("topSongsCountLabel", { count: topSongs.length }) ||
              `Top ${topSongs.length}`}
          </span>
        )}
      </div>

      {loading ? (
        <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
          {[1, 2, 3, 4, 5].map((i) => (
            <li key={i} className="h-[72px] px-4 sm:px-5 animate-pulse">
              <div className="h-full flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700" />
                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-2/3 rounded bg-gray-100 dark:bg-gray-700" />
                  <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-gray-700" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : topSongs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
            <HiOutlineMusicalNote className="w-7 h-7" />
          </div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("noTopSongsYet") || "No song stats yet"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
            {t("noTopSongsYetHint") ||
              "Add songs to events and they'll show up here."}
          </p>
        </div>
      ) : (
        <ol className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
          {topSongs.map((song, index) => (
            <li key={song.songId} className="group">
              <div className="flex items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-3 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors">
                <span
                  className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-semibold tabular-nums ${
                    index === 0
                      ? "bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd]"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {index + 1}
                </span>
                <Link
                  to={`/songs/${song.songId}`}
                  className="flex items-center gap-3 min-w-0 flex-1"
                >
                  <div className="relative w-12 h-12 shrink-0 overflow-hidden rounded-xl ring-1 ring-gray-200/70 dark:ring-gray-600/70">
                    <SongArtwork
                      src={song.artworkURL}
                      alt={song.title}
                      className="!rounded-none w-full h-full object-cover"
                      iconClassName="w-6 h-6"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate group-hover:text-[#5932EA] transition-colors">
                      {song.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {song.artist}
                      <span className="text-gray-300 dark:text-gray-600 mx-1.5" aria-hidden>
                        ·
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-amber-700 dark:text-amber-300 font-medium tabular-nums">
                        <HiOutlineStar className="w-3 h-3" />
                        {song.count}×
                      </span>
                    </p>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => handleOpenModal(song)}
                  className="shrink-0 p-2.5 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors touch-manipulation"
                  title={t("viewEvents") || "View events"}
                  aria-label={t("viewEvents") || "View events"}
                >
                  <HiOutlineEye className="w-4 h-4" />
                </button>
                <Link
                  to={`/songs/${song.songId}`}
                  className="hidden sm:inline-flex shrink-0 items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-[#5932EA]/35 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] hover:bg-[#5932EA]/18 transition-colors"
                >
                  {t("viewSong") || "View song"}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      )}

      {modalOpen && selectedSong && (
        <AnimatedModal
          isOpen
          onClose={handleCloseModal}
          contentClassName="sm:max-w-lg"
        >
          <div className={modalPanelClass}>
            <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-xl overflow-hidden ring-1 ring-gray-200/70 dark:ring-gray-600 shrink-0">
                  <SongArtwork
                    src={selectedSong.artworkURL}
                    alt={selectedSong.title}
                    className="!rounded-none w-full h-full object-cover"
                    iconClassName="w-6 h-6"
                  />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {selectedSong.title}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {selectedSong.artist}
                    {" · "}
                    {t("eventsUsingSong") || "Events using this song"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={t("close")}
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 overflow-y-auto flex-1 min-h-0">
              {eventsLoading ? (
                <div className="flex justify-center py-10">
                  <span className="w-8 h-8 border-2 border-[#5932EA]/25 border-t-[#5932EA] rounded-full animate-spin" />
                </div>
              ) : relatedEvents.length > 0 ? (
                <ul className="space-y-2">
                  {relatedEvents.map((event) => (
                    <li key={event.id}>
                      <Link
                        to={
                          isAdmin
                            ? `/events/${event.id}`
                            : `/my-events/${event.id}`
                        }
                        onClick={handleCloseModal}
                        className="flex items-center gap-3 p-3 rounded-xl border border-gray-200/70 dark:border-gray-700/70 bg-white/50 dark:bg-gray-900/30 hover:border-[#5932EA]/30 hover:bg-[#5932EA]/5 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
                          <HiOutlineCalendarDays className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-sm text-gray-800 dark:text-gray-100 block truncate">
                            {event.title ||
                              t("untitledEvent") ||
                              "Untitled event"}
                          </span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {[
                              event.date,
                              event.status === "Archived"
                                ? t("archived")
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                  {t("noRelatedEvents") || "No events found"}
                </p>
              )}
            </div>
            <div className="px-4 sm:px-6 py-4 border-t border-gray-100/80 dark:border-gray-700/80 shrink-0">
              <Link
                to={`/songs/${selectedSong.songId}`}
                onClick={handleCloseModal}
                className={`${glassBtn} w-full`}
              >
                {t("viewSong") || "View song"}
              </Link>
            </div>
          </div>
        </AnimatedModal>
      )}
    </div>
  );
};

export default TopSongs;
